// app/src/lib/session.tsx — auth/session state.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { SessionUser } from "../../../shared/types";
import { api, ApiError } from "./api";

interface LoginResult { otp_required?: boolean; pending_token?: string; sent_to?: string; channel?: "whatsapp" | "email" }
interface SessionCtx {
  user: SessionUser | null;
  loading: boolean;
  login: (creds: { email?: string; password?: string; pin?: string; organisation_id?: string; user_id?: string }) => Promise<LoginResult>;
  verifyOtp: (pending_token: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<SessionCtx>(null as never);
export const useSession = () => useContext(Ctx);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<SessionUser>("/auth/me")
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  // Server-side session end (idle timeout / device or IP change / revoked) -> back to login.
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener("epos:session-expired", onExpired);
    return () => window.removeEventListener("epos:session-expired", onExpired);
  }, []);

  const login: SessionCtx["login"] = async (creds) => {
    const r = await api.post<LoginResult>("/auth/login", creds);
    if (r?.otp_required) return r;            // caller must collect the emailed code
    setUser(await api.get<SessionUser>("/auth/me"));
    return r ?? {};
  };
  const verifyOtp = async (pending_token: string, code: string) => {
    await api.post("/auth/verify-otp", { pending_token, code });
    setUser(await api.get<SessionUser>("/auth/me"));
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { if (!(e instanceof ApiError)) throw e; }
    setUser(null);
  };

  return <Ctx.Provider value={{ user, loading, login, verifyOtp, logout }}>{children}</Ctx.Provider>;
}
